import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import { ErrorText } from '@/components/basicComponents/input';
import { allowFileExtensions } from '@/components/settings-page/importData/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { Button, insertMarkdown$, MDXEditorMethods, usePublisher } from '@mdxeditor/editor';
import { MutableRefObject, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

const fileExtensions = ['JPEG', 'PNG', 'GIF', 'JPG'];

const getImageSize = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = (err) => reject(err);
  });
};

interface ImageUploadProps {
  recordId: string;
  fieldId: string;
  fieldAnswerId: string;
  markdownEditorRef: MutableRefObject<MDXEditorMethods>;
  handleUploadAttachment: ({
    file,
    recordId,
    fieldId,
    fieldAnswerId
  }: {
    file: File;
    recordId: string;
    fieldId: string;
    fieldAnswerId: string;
  }) => Promise<{
    id: string;
    url: string;
    assetKey: string;
  }>;
  updateLocalAttachments: ({ id, url, assetKey }: { id: string; url: string; assetKey: string }) => void;
}

const ImageUpload = ({ recordId, fieldId, fieldAnswerId, markdownEditorRef, handleUploadAttachment, updateLocalAttachments }: ImageUploadProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setValue, reset, handleSubmit } = useForm({ defaultValues: { file: null } });

  const insertMarkdown = usePublisher(insertMarkdown$);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNotSupportedDialog, setShowNotSupportedDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      reset({ file: null });
    }
  }, [isOpen]);

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);
    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setShowNotSupportedDialog(true);
      return;
    }
    setValue('file', uploadFiles[0], { shouldValidate: true });
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;
    if (!allowFileExtensions(uploadFiles, fileExtensions).length) {
      setShowNotSupportedDialog(true);
      return;
    }
    setValue('file', uploadFiles[0], { shouldValidate: true });
  };

  const deleteFilePreview = () => {
    setValue('file', null, { shouldValidate: true });
  };

  const onSave = async (data: { file: File }) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { file } = data;
      const { id, url, assetKey } = await handleUploadAttachment({ file, recordId, fieldId, fieldAnswerId });

      const { width, height } = await getImageSize(file);
      markdownEditorRef?.current?.focus();
      insertMarkdown(`<img height="${height}" width="${width}" alt="${file.name}" title="${file.name}" src="${url}" />`);
      updateLocalAttachments({ id, url, assetKey });

      setIsOpen(false);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (showNotSupportedDialog) {
    return <FileNotSupported isOpen={showNotSupportedDialog} onClose={() => setShowNotSupportedDialog(false)} />;
  }

  return (
    <>
      <Button onClick={() => setIsOpen(true)}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.11537 20.5C4.6182 20.5 4.19262 20.323 3.83862 19.969C3.48462 19.615 3.30762 19.1894 3.30762 18.6922V5.30773C3.30762 4.81056 3.48462 4.38498 3.83862 4.03098C4.19262 3.67698 4.6182 3.49998 5.11537 3.49998H13.8076V4.99998H5.11537C5.02553 4.99998 4.95178 5.02881 4.89412 5.08648C4.83645 5.14415 4.80762 5.2179 4.80762 5.30773V18.6922C4.80762 18.7821 4.83645 18.8558 4.89412 18.9135C4.95178 18.9711 5.02553 19 5.11537 19H18.4999C18.5895 19 18.6633 18.9711 18.7211 18.9135C18.7788 18.8558 18.8076 18.7821 18.8076 18.6922V9.99998H20.3076V18.6922C20.3076 19.1894 20.1306 19.615 19.7766 19.969C19.4226 20.323 18.997 20.5 18.4999 20.5H5.11537ZM17.1921 8.61523V6.61523H15.1921V5.11548H17.1921V3.11548H18.6921V5.11548H20.6921V6.61523H18.6921V8.61523H17.1921ZM6.55762 16.75H17.1344L13.8459 12.3655L11.0384 16.0192L9.03837 13.4615L6.55762 16.75Z"
            fill="currentColor"
          ></path>
        </svg>
      </Button>
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Image"
        confirmButton={{
          name: 'Save',
          onClick: handleSubmit(onSave),
          disabled: loading
        }}
        showCloseIcon={!loading}
        showCancelButton={!loading}
        isUseOutsideClick={!loading || showNotSupportedDialog}
      >
        {loading ? (
          <ModalLoader />
        ) : (
          <>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <Controller
              name="file"
              control={control}
              rules={{
                validate: {
                  hasFiles: (file: File) => !!file || 'File is required'
                }
              }}
              render={({ field: { value: file }, fieldState: { error } }) => (
                <div>
                  {!!file && (
                    <FileUpload
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      type={file.type.includes('image') ? 'image' : 'document'}
                      imageSrc={URL.createObjectURL(file)}
                      fileName={file.name}
                      deleteFile={deleteFilePreview}
                    />
                  )}
                  {!file && (
                    <DropFileArea
                      id="signed-signature-packet"
                      handleUploadFile={handleUploadFile}
                      onDropHandler={onDropHandler}
                      fileExtensions={fileExtensions}
                      drag={drag}
                      setDrag={setDrag}
                    />
                  )}
                  {error?.message && <ErrorText size="md">{error?.message}</ErrorText>}
                </div>
              )}
            />
          </>
        )}
      </ModalWrappedContent>
    </>
  );
};

export default ImageUpload;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
